import Immutable from 'immutable';
import type Luminary from '@lumen5/luminary';
import type { AspectRatio, OpaqueVideoData, TemplateBundle } from '@lumen5/luminary';
import { TemplateColorProperties, VideoData } from '@lumen5/luminary';
import { createSelector } from '@reduxjs/toolkit';

import type { DisplayGroup, SceneDesign } from 'js/src/client';
import { GlobalTemplate, TemplateStatusEnum } from 'js/src/client';
import { CLIP_CREATOR_PREVIEW_TEMPLATE } from 'js/src/data/clip-creator/clip-creator-consts';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import {
    isTemplatesListRequestFetched,
    isTemplatesListRequestFetching,
} from 'js/src/data/templates/slices/loadingInfoSlice';
import type {
    ChartColorOverrides,
    DisplayGroupId,
    PaletteData,
    QATemplateBundle,
    StoredDisplayGroup,
    StoredSceneDesignIdsPerTemplate,
    StoredTemplate,
    StoredTemplateCategory,
} from 'js/src/data/templates/templates-types';
import type { Template as ValidationTemplate } from 'js/src/editor/validation/validationTypes';
import type { DeepReadonly, ImmutableMap, TemplateCategoryId } from 'js/src/libs/consts';
import {
    DYNAMIC_DISPLAY_GROUPS,
    ImmutableFromJS,
    SceneDesignSpecialCase,
    toMutableCopy,
    withStableEmptyArray,
} from 'js/src/libs/consts';
import { int2hex } from 'js/src/libs/css-helpers';
import Flags from 'js/src/libs/flags';
import type { RootState } from 'js/src/libs/RootState';
// Categories
const templateCategoriesOrm = (state: RootState) =>
    new PlainJsStoreORM<StoredTemplateCategory, TemplateCategoryId>(
        state.templates.templateCategories,
    );

export function getCategoryIds(state: RootState): readonly TemplateCategoryId[] {
    return templateCategoriesOrm(state).allIds;
}

/**
 * Category IDs shown to users when selecting a template, excluding any categories
 * marked as hidden. Hidden categories remain available to internal template editing
 * tools (see getCategoryIds).
 */
export const getTemplateCategoryIds = createSelector(
    [(state: RootState) => state.templates.templateCategories],
    (templateCategories): readonly TemplateCategoryId[] => {
        const templateCategoryOrm = new PlainJsStoreORM<StoredTemplateCategory, TemplateCategoryId>(
            templateCategories,
        );
        return withStableEmptyArray(
            templateCategoryOrm.allIds.filter(
                (categoryId) => !templateCategoryOrm.getByIdOrThrow(categoryId).hidden,
            ),
        );
    },
);

export function getTemplateIdsForACategory(
    state: RootState,
    categoryId: TemplateCategoryId,
): readonly string[] {
    return templateCategoriesOrm(state).getByIdOrThrow(categoryId).templates;
}

export function getCategoryById(
    state: RootState,
    categoryId: TemplateCategoryId,
): StoredTemplateCategory {
    return templateCategoriesOrm(state).getByIdOrThrow(categoryId);
}

export function getDraftCategoryId(state: RootState): number {
    // Returns the [DRAFT] category if it can be found, otherwise falls back to the first category
    const orm = templateCategoriesOrm(state);
    const draftCategory = orm.find((category) => category.display_name === '[DRAFT]');
    if (draftCategory) {
        return draftCategory.id;
    }
    const firstCategoryId = orm.allIds[0];
    if (firstCategoryId === undefined) {
        throw new Error('No categories found');
    }
    return firstCategoryId;
}

// Templates
const templatesListOrm = (templatesList: RootState['templates']['templatesList']) =>
    new PlainJsStoreORM<StoredTemplate, string>(templatesList);

const immutableTemplateValues = new WeakMap<object, unknown>();

const toImmutableTemplateValue = (value: unknown): unknown => {
    if (typeof value !== 'object' || value === null) {
        return value;
    }
    if (!immutableTemplateValues.has(value)) {
        immutableTemplateValues.set(value, Immutable.fromJS(value));
    }
    return immutableTemplateValues.get(value);
};

export function selectStoredTemplate(
    state: RootState,
    templateId: string,
): StoredTemplate | undefined {
    return templatesListOrm(state.templates.templatesList).getById(templateId);
}

export function selectStoredTemplateIds(state: RootState): readonly string[] {
    return state.templates.templatesList.allIds;
}

export function hasTemplate(state: RootState, templateId: string): boolean {
    return !!selectStoredTemplate(state, templateId);
}

export function areTemplatesLoading(state: RootState): boolean {
    return isTemplatesListRequestFetching(state);
}

export const areTemplatesLoaded = (state: RootState): boolean => {
    const templatesListSize = templatesListOrm(state.templates.templatesList).length;
    return isTemplatesListRequestFetched(state) && templatesListSize > 0;
};

export const areAllSceneDesignsLoadedForTemplate = (
    state: RootState,
    templateId: string,
): boolean => {
    const templateSceneIds = getTemplateSceneIds(state, templateId);
    const allScenesFetched = templateSceneIds.every((sceneId) => !!getSceneDesign(state, sceneId));
    return templateSceneIds.length > 0 && allScenesFetched;
};

export function isTemplateBundleLoading(state: RootState, templateId: string): boolean {
    const templateBundle = getTemplateBundle(state, templateId);
    return templateBundle === null || Object.keys(templateBundle.sceneDesigns).length === 0;
}

export const templatesLoadedStatuses = createSelector(
    (state: RootState) => state.templates.templatesList,
    (state: RootState) => state.templates.sceneDesignIdsByTemplate,
    (state: RootState) => state.templates.sceneDesigns,
    (templatesList, sceneDesignIdsByTemplate, sceneDesigns) => {
        const templatesOrm = templatesListOrm(templatesList);
        const sceneDesignsIdsOrm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(
            sceneDesignIdsByTemplate,
        );
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesigns);
        const templateIds = templatesOrm.allIds;
        const loadedTemplateIds = templateIds.filter((templateId: string) => {
            const template = templatesOrm.getById(templateId);
            if (!template || !template.template_data) {
                return false;
            }
            const sceneDesignIds = sceneDesignsIdsOrm.getByIdOrThrow(templateId).scene_design_ids;
            if (sceneDesignIds.length === 0) {
                return false;
            }
            return sceneDesignIds.every((id) => !!sceneDesignsOrm.getById(id));
        });
        const statuses: Readonly<Record<string, boolean>> = Object.fromEntries(
            loadedTemplateIds.map((templateId) => [templateId, true]),
        );
        return statuses;
    },
);

export function getTemplateIds(state: RootState): readonly string[] {
    return selectStoredTemplateIds(state);
}

export const selectTemplateIds = selectStoredTemplateIds;

export const selectTemplates = createSelector(
    [(state: RootState) => state.templates.templatesList],
    (templatesList): readonly StoredTemplate[] =>
        withStableEmptyArray(templatesListOrm(templatesList).map((template) => template)),
);

export const getTemplateById = selectStoredTemplate;

/**
 * The one template a user can actually pick, when their account has exactly a
 * single usable (non-global) template — e.g. a restricted enterprise workspace
 * with one custom template and no public catalog beside it. Returns undefined
 * when there's a genuine choice (or the list hasn't loaded yet), so callers fall
 * back to asking rather than forcing an answer.
 */
export const selectSoleUsableTemplate = createSelector(
    [(state: RootState) => state.templates.templatesList],
    (templatesList): StoredTemplate | undefined => {
        const usable = templatesListOrm(templatesList).filter(
            (template) => !template.is_global_template,
        );
        return usable.length === 1 ? usable[0] : undefined;
    },
);

/**
 * Before call operations on luminary
 * which require scene designs or template data (addSlide, set aspectRatio)
 * we should pass all information about sceneDesigns and template.
 * Object which contains this information called templateBundle
 * In redux store we keep templates and sceneDesigns separately and we use redux selectors
 * in order to create templateBundle object.
 * This cache contains templateBundles
 */
type TemplateBundleCacheEntry = {
    bundle: TemplateBundle;
    templateDataRef: unknown;
    sceneDesignRefs: Record<string, unknown>; // PlainJsStoreORM references per scene design
};

const templateBundlesCache = new Map<string, TemplateBundleCacheEntry | null>();

export function getTemplateBundle(state: RootState, templateId: string): TemplateBundle | null {
    const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(state.templates.sceneDesigns);

    const template = selectStoredTemplate(state, templateId);
    const sceneDesignIds = getTemplateSceneIds(state, templateId);

    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);

    if (!template || sceneDesignIds.length === 0) {
        templateBundlesCache.set(templateId, null);
        return null;
    }

    const templateData = template.template_data as DeepReadonly<OpaqueVideoData> | undefined;
    if (!templateData) {
        templateBundlesCache.set(templateId, null);
        return null;
    }

    // Check if cached entry is still valid by comparing source references
    const cached = templateBundlesCache.get(templateId);
    if (cached) {
        let cacheValid = cached.templateDataRef === templateData;
        if (cacheValid && Object.keys(cached.sceneDesignRefs).length !== sceneDesignIds.length) {
            cacheValid = false;
        }
        if (cacheValid) {
            sceneDesignIds.forEach((sceneDesignId: string) => {
                const sceneDesignData = sceneDesignsOrm.getById(sceneDesignId);
                if (cached.sceneDesignRefs[sceneDesignId] !== sceneDesignData) {
                    cacheValid = false;
                }
            });
        }
        if (cacheValid) {
            return cached.bundle;
        }
    }

    const sceneDesigns: TemplateBundle['sceneDesigns'] = {};
    const sceneDesignRefs: Record<string, unknown> = {};

    sceneDesignIds.forEach((sceneDesignId: string) => {
        const sceneDesignData = sceneDesignsOrm.getById(sceneDesignId);
        if (!sceneDesignData) {
            return;
        }

        sceneDesignRefs[sceneDesignId] = sceneDesignData;
        const aspectRatioMap = {} as { [K in AspectRatio]: OpaqueVideoData | null };
        VideoData.ALL_ASPECT_RATIOS.forEach((aspectRatio) => {
            if (!supportedAspectRatios.includes(aspectRatio)) {
                return;
            }
            const aspectRatioDataKey = `${aspectRatio}_data` as keyof SceneDesign;
            const aspectRatioData = sceneDesignData[aspectRatioDataKey] as OpaqueVideoData;
            if (!aspectRatioData) {
                return;
            }

            aspectRatioMap[aspectRatio] = aspectRatioData;
        });

        if (Object.keys(aspectRatioMap).length > 0) {
            sceneDesigns[sceneDesignId] = aspectRatioMap;
        }
    });

    const bundle: TemplateBundle = {
        templateData: toMutableCopy<OpaqueVideoData>(templateData),
        sceneDesigns,
    };

    templateBundlesCache.set(templateId, {
        bundle,
        templateDataRef: templateData,
        sceneDesignRefs,
    });
    return bundle;
}

export const getSupportedAspectRatios = createSelector(
    [
        (state: RootState, templateId: string | null) => templateId,
        (state: RootState, templateId: string | null) => {
            if (!templateId) {
                return null;
            }
            return getTemplateById(state, templateId) || null;
        },
    ],
    (templateId, template) => {
        if (!templateId || !template) {
            return [] as AspectRatio[];
        }

        const supportedAspectRatios = (template.supported_aspect_ratios ??
            []) as readonly AspectRatio[];

        return [...supportedAspectRatios];
    },
);

const EMPTY_NUMBERS: readonly number[] = [];
const EMPTY_STRINGS: readonly string[] = [];

export function getTemplateTeams(state: RootState, templateId: string | null): readonly number[] {
    if (!templateId) {
        return EMPTY_NUMBERS;
    }
    return selectStoredTemplate(state, templateId)?.teams ?? EMPTY_NUMBERS;
}

type QATemplateBundleCacheEntry = {
    bundle: QATemplateBundle;
    baseBundleRef: TemplateBundle;
};

const qaTemplateBundleCache = new Map<string, QATemplateBundleCacheEntry | null>();

export function getQATemplateBundle(state: RootState, templateId: string): QATemplateBundle | null {
    const baseBundle = getTemplateBundle(state, templateId);
    if (!baseBundle) {
        qaTemplateBundleCache.set(templateId, null);
        return null;
    }

    const cached = qaTemplateBundleCache.get(templateId);
    if (cached && cached.baseBundleRef === baseBundle) {
        return cached.bundle;
    }

    const sceneDesignIds = getTemplateSceneIds(state, templateId);

    const template = templatesListOrm(state.templates.templatesList).getByIdOrThrow(templateId);

    const sceneDesigns: QATemplateBundle['sceneDesigns'] = {
        ...baseBundle.sceneDesigns,
    } as QATemplateBundle['sceneDesigns'];
    sceneDesignIds.forEach((sceneDesignId: string) => {
        const sceneAttributesRecommendation = getSceneAttributesRecommendation(
            state,
            sceneDesignId,
        );
        if (sceneAttributesRecommendation && sceneDesigns[sceneDesignId]) {
            sceneDesigns[sceneDesignId] = {
                ...sceneDesigns[sceneDesignId],
                scene_attributes_recommendation: sceneAttributesRecommendation,
            };
        }
    });

    const paletteData = template.palette_data as DeepReadonly<PaletteData[]> | undefined;
    const paletteDatas = paletteData ? toMutableCopy<PaletteData[]>(paletteData) : [];

    const bundle: QATemplateBundle = {
        ...baseBundle,
        sceneDesigns,
        paletteDatas,
        textMinSize: template.text_min_size,
        textMaxSize: template.text_max_size,
    };

    qaTemplateBundleCache.set(templateId, { bundle, baseBundleRef: baseBundle });
    return bundle;
}

/**
 * Creates a special bundle of data that is passed to the validator.
 */
export function getTemplateValidationData(
    state: RootState,
    templateId: string,
): ImmutableMap<ValidationTemplate> {
    const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(state.templates.sceneDesigns);
    const displayGroupsOrm = new PlainJsStoreORM<StoredDisplayGroup, DisplayGroupId>(
        state.templates.displayGroups,
    );
    const template = ImmutableFromJS(
        templatesListOrm(state.templates.templatesList).getByIdOrThrow(templateId),
    ) as ImmutableMap<ValidationTemplate>;
    const sceneDesignIds = getTemplateSceneIds(state, templateId);
    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);
    const templateDisplayGroups = toMutableCopy<DisplayGroup[]>(
        getTemplateDisplayGroups(state, templateId),
    );
    let validationData = template;

    if (!(template && sceneDesignsOrm.length > 0)) {
        throw new Error(`Template with id ${templateId} not found`);
    }

    validationData = validationData.set('scene_designs', Immutable.List());
    sceneDesignIds.forEach((sceneDesignId, index) => {
        const sceneDesignData = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
        validationData = validationData.setIn(['scene_designs', index, 'id'], sceneDesignData.id);
        validationData = validationData.setIn(
            ['scene_designs', index, 'status'],
            sceneDesignData.status,
        );
        supportedAspectRatios.forEach((aspectRatio) => {
            const aspectRatioDataKey = `${aspectRatio}_data` as keyof SceneDesign;
            const aspectRatioData = sceneDesignData[aspectRatioDataKey];
            if (!aspectRatioData) {
                return;
            }
            validationData = validationData.setIn(
                ['scene_designs', index, aspectRatioDataKey],
                aspectRatioData,
            );
        });
        validationData = validationData.setIn(
            ['scene_designs', index, 'scene_attributes_recommendation'],
            getSceneAttributesRecommendation(state, sceneDesignId),
        );
        validationData = validationData.setIn(
            ['scene_designs', index, 'display_group'],
            sceneDesignData.display_group,
        );
        validationData = validationData.setIn(
            ['scene_designs', index, 'non_recommendable_assets'],
            sceneDesignData.non_recommendable_assets,
        );
        const displayGroup = toMutableCopy<DisplayGroup>(
            displayGroupsOrm.getByIdOrThrow(sceneDesignData.display_group),
        );
        validationData = validationData.setIn(
            ['scene_designs', index, 'objects', 'display_group'],
            displayGroup,
        );
        validationData = validationData.setIn(
            ['scene_designs', index, 'objects', 'display_groups'],
            templateDisplayGroups,
        );
    });

    return validationData;
}

export function getSceneDesign(
    state: RootState,
    sceneDesignId: string | null | undefined,
): SceneDesign | undefined | null {
    if (!sceneDesignId) return null;
    const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(state.templates.sceneDesigns);
    return sceneDesignsOrm.getById(sceneDesignId);
}

export function getSceneAttributesRecommendation(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    return sceneDesign && sceneDesign.scene_attributes_recommendation;
}

export function getSceneDesignStatus(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    return sceneDesign && sceneDesign.status;
}

export function getSceneDesignDisplayName(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    const displayName = sceneDesign && sceneDesign.display_name;
    return displayName !== null ? displayName : '';
}

export function getTemplateData(
    state: RootState,
    templateId: string,
): DeepReadonly<OpaqueVideoData> | undefined {
    return selectStoredTemplate(state, templateId)?.template_data as
        | DeepReadonly<OpaqueVideoData>
        | undefined;
}

export function getTemplateStatus(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.status;
}

export function getTemplateDefaultTextSizeOptions(
    state: RootState,
    templateId: string,
): readonly number[] | undefined {
    return selectStoredTemplate(state, templateId)?.default_text_size_options;
}

export function getTemplateDisplayName(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.display_name;
}

export function getTemplateImageUrl(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.image_url;
}

export function getTemplateMp4Url(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.mp4_url;
}

export function getTemplateAspectRatioMp4Urls(
    state: RootState,
    templateId: string,
): Record<AspectRatio, string> | null {
    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);

    if (supportedAspectRatios.length === 0) {
        return null;
    }

    const template = getTemplateById(state, templateId);
    if (!template) {
        return null;
    }
    const templateMp4Urls: Record<AspectRatio, string> = Object.fromEntries(
        supportedAspectRatios
            .map((aspectRatio: AspectRatio) => {
                const url = template[`${aspectRatio}_mp4_url`];
                return url ? [aspectRatio, url] : null;
            })
            .filter((entry): entry is [AspectRatio, string] => entry !== null),
    ) as Record<AspectRatio, string>;

    return templateMp4Urls;
}

export function getTemplateTextMinSize(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.text_min_size;
}

export function getTemplateTextMaxSize(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.text_max_size;
}

export function getTemplateTextDefaultSizes(
    state: RootState,
    templateId: string,
): readonly number[] {
    return selectStoredTemplate(state, templateId)?.default_text_size_options ?? EMPTY_NUMBERS;
}

export function getTemplateColorPalettes(
    state: RootState,
    templateId: string,
): Immutable.List<ImmutableMap<PaletteData>> {
    const template = selectStoredTemplate(state, templateId);
    if (!template || template.palette_data === undefined) {
        return Immutable.List();
    }
    return toImmutableTemplateValue(template.palette_data) as Immutable.List<
        ImmutableMap<PaletteData>
    >;
}

export function getChartColorOverrides(state: RootState, templateId: string): ChartColorOverrides {
    const template = getTemplateById(state, templateId);
    const overrides = toImmutableTemplateValue(template?.chart_color_overrides) as
        | ChartColorOverrides
        | undefined;
    return overrides ?? Immutable.Map<string, string>();
}

/**
 * A palette's colors as hex. Luminary stores them as packed RGB integers, but hex is what the
 * agent's color tools accept and what the color pickers render, so anything leaving the palette
 * data for one of those consumers goes through here.
 */
export function getPaletteColorsAsHex(palette: ImmutableMap<PaletteData>): string[] {
    return palette
        .get('colors')
        .toArray()
        .map((color) => int2hex(color.get(TemplateColorProperties.COLOR)));
}

// returns color codes for the template's default palettes, where each palette is represented by an array
export const getTemplatePaletteColorCodes = createSelector(
    (state: RootState, templateId: string) => getTemplateColorPalettes(state, templateId),
    (palettes) => {
        return palettes.toArray().map((palette) => {
            const paletteTemplateColors = palette.get('colors').toArray();
            return paletteTemplateColors.map((color) => color.get(TemplateColorProperties.COLOR));
        });
    },
);

export function getTemplateDefaultSceneDesign(state: RootState, templateId: string): string | null {
    // default_scene_design should be set for all templates except super old templates
    const template = getTemplateById(state, templateId);
    return template?.default_scene_design ?? null;
}

export function getTemplateDefaultTalkingHeadSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_talking_head_scene_design is required for any templates that support talking head scenes
    const template = getTemplateById(state, templateId);
    return template?.default_talking_head_scene_design ?? null;
}

export function getTemplateDefaultCutawaySceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_cutaway_scene_design is required for any templates that support voiceover scenes
    const template = getTemplateById(state, templateId);
    return template?.default_cutaway_scene_design ?? null;
}

export function getTemplateDefaultStoryboardSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_storyboard_scene_design should be set for all templates except super old templates
    const template = getTemplateById(state, templateId);
    return template?.default_storyboard_scene_design ?? null;
}

export function getTemplateDefaultOutroSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_outro_scene_design should be set for all templates except super old templates
    const template = getTemplateById(state, templateId);
    return template?.default_outro_scene_design ?? null;
}

export function getTemplateDefaultIconSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_icon_scene_design should be set for all templates that support icons (pretty much all of them!)
    const template = getTemplateById(state, templateId);
    return template?.default_icon_scene_design ?? null;
}

export function getTemplateDefaultCaptionSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_caption_scene_design should be set for all templates that support voiceover or talking head
    const template = getTemplateById(state, templateId);
    return template?.default_caption_scene_design ?? null;
}

export function getTemplateDefaultVoiceoverSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_voiceover_scene_design is required for any templates that support voiceover scenes
    const template = getTemplateById(state, templateId);
    return template?.default_voiceover_scene_design ?? null;
}

export function getSceneCategoryFromSceneDesign(
    sceneDesign: SceneDesign,
    supportedAspectRatios: AspectRatio[],
): string {
    const aspectRatio = supportedAspectRatios[0];
    const firstSupportedAspectRatioKey = `${aspectRatio}_data` as keyof SceneDesign;

    return VideoData.getSceneCategoryFromSceneData(
        sceneDesign[firstSupportedAspectRatioKey] as OpaqueVideoData,
    );
}

const sceneDesignIdsByTemplateOrm = (state: RootState) =>
    new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(
        state.templates.sceneDesignIdsByTemplate,
    );

export function getTemplateSceneIds(
    state: RootState,
    templateId: string | null,
    opts?: { excludeGlobalTemplate?: boolean },
): readonly string[] {
    const sceneDesignIds = templateId
        ? sceneDesignIdsByTemplateOrm(state).getById(templateId)
        : undefined;
    if (!sceneDesignIds) {
        return withStableEmptyArray([]);
    }
    const ids = withStableEmptyArray(sceneDesignIds.scene_design_ids);
    if (opts?.excludeGlobalTemplate) {
        return withStableEmptyArray(
            ids.filter((id) => VideoData.getTemplateIdFromSceneDesignId(id) === templateId),
        );
    }
    return ids;
}

export function getAllSceneDesignIdsPerTemplate(
    state: RootState,
): Readonly<Record<string, StoredSceneDesignIdsPerTemplate>> {
    return state.templates.sceneDesignIdsByTemplate.byId;
}

export const getTemplateSceneDesigns = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) =>
        getTemplateDefaultTalkingHeadSceneDesign(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (
        sceneDesignsInState,
        sceneDesignIds,
        defaultTalkingHeadSceneDesign,
        supportedAspectRatios,
    ): SceneDesign[] => {
        if (supportedAspectRatios.length === 0) {
            return [];
        }
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        sceneDesignIds.forEach((sceneDesignId) => {
            const sceneDesign = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (canUserSee(sceneDesign, sceneCategory, defaultTalkingHeadSceneDesign)) {
                result.push(sceneDesign);
            }
        });
        return result;
    },
);

export const getCaptionsSceneDesignsForTemplate = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneDesignsInState, templateSceneDesigns, supportedAspectRatios): SceneDesign[] => {
        if (supportedAspectRatios.length === 0) {
            return [];
        }
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        templateSceneDesigns.forEach((sceneDesignId) => {
            const sceneDesign = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (
                sceneCategory === VideoData.CATEGORY_CAPTION &&
                sceneDesign.status === TemplateStatusEnum.ACTIVE
            ) {
                result.push(sceneDesign);
            }
        });
        return result;
    },
);

export const getVoiceoverSceneDesignsForTemplate = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneDesignsInState, templateSceneDesigns, supportedAspectRatios): SceneDesign[] => {
        if (supportedAspectRatios.length === 0) {
            return [];
        }
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        templateSceneDesigns.forEach((sceneDesignId) => {
            const sceneDesign = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (
                sceneCategory === VideoData.CATEGORY_VOICEOVER &&
                sceneDesign.status === TemplateStatusEnum.ACTIVE
            ) {
                result.push(sceneDesign);
            }
        });
        return result;
    },
);

/*
 * getTemplateSceneDesigns is used in the app as well as in the editor.
 * Unfortunately that means that some scene designs are filtered out
 * of the editor depending on the feature flags of the internal users
 * which is not what we want. This method is only to be used for
 * internal apps, not for customers.
 */
export const INTERNAL_getTemplateSceneDesigns = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string | null) => getTemplateSceneIds(state, templateId),
    (sceneDesignsInState, sceneDesignIds): SceneDesign[] => {
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        sceneDesignIds.forEach((sceneDesignId) => {
            result.push(sceneDesignsOrm.getByIdOrThrow(sceneDesignId));
        });
        return result;
    },
);

export const getFullscreenSelectorSceneDesigns = createSelector(
    (state: RootState, templateId: string) => getTemplateSceneDesigns(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneDesigns, supportedAspectRatios): SceneDesign[] =>
        sceneDesigns.filter((sceneDesign) => {
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (sceneCategory === VideoData.CATEGORY_CAPTION) {
                return false;
            }
            if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT) {
                return false;
            }
            if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD) {
                return false;
            }
            if (sceneCategory === VideoData.CATEGORY_VOICEOVER) {
                return false;
            }
            return true;
        }),
);

/**
 * Scene designs to render in the template gallery previews: the fullscreen-selector set,
 * minus designs imported from enabled global templates.
 */
export const getTemplatePreviewSceneDesigns = createSelector(
    getFullscreenSelectorSceneDesigns,
    (_state: RootState, templateId: string) => templateId,
    (sceneDesigns, templateId): SceneDesign[] =>
        sceneDesigns.filter((sceneDesign) => sceneDesign.template === templateId),
);

export const getTemplateSceneDesignsByCategory = createSelector(
    (state: RootState, templateId: string, sceneCategory: string) => sceneCategory,
    (state: RootState, templateId: string) => getTemplateSceneDesigns(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneCategory, allSceneDesigns, supportedAspectRatios): SceneDesign[] =>
        allSceneDesigns.filter((sceneDesign) => {
            const actualSceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            return sceneCategory === actualSceneCategory;
        }),
);

export function getLastSceneDesignOrder(state: RootState, templateId: string): number {
    const ids = getTemplateSceneIds(state, templateId);
    if (ids.length === 0) {
        return -1;
    }

    // To be safe, assume the order isn't a sequence, 0, 1, 2, 3 but could be 0, 1, 1, 5.
    const sceneDesign = getSceneDesign(state, ids[ids.length - 1]);

    if (!sceneDesign) {
        return -1;
    }

    return sceneDesign.order;
}

function canUserSee(
    sceneDesign: SceneDesign,
    sceneCategory: string,
    defaultTalkingHeadSceneDesign: string | null,
): boolean {
    const isTalkingHead = sceneCategory === VideoData.CATEGORY_TALKING_HEAD;
    if (isTalkingHead && sceneDesign.id !== defaultTalkingHeadSceneDesign) {
        return false;
    }
    return Flags.canSeeAllTemplates() || sceneDesign.status === TemplateStatusEnum.ACTIVE;
}

export function canUserSeeSceneDesign(state: RootState, sceneDesignId: string): boolean {
    if (Flags.canSeeAllTemplates()) {
        return true;
    }

    const status = getSceneDesignStatus(state, sceneDesignId);
    return status === TemplateStatusEnum.ACTIVE;
}

const displayGroupsOrm = (displayGroups: RootState['templates']['displayGroups']) =>
    new PlainJsStoreORM<StoredDisplayGroup, DisplayGroupId>(displayGroups);

const byDisplayGroupOrder = (dg1: StoredDisplayGroup, dg2: StoredDisplayGroup) =>
    dg1.order - dg2.order;

export const getTemplateDisplayGroups = createSelector(
    (state: RootState) => state.templates.displayGroups,
    (state: RootState, templateId: string) => templateId,
    (state: RootState, templateId: string, opts?: { excludeGlobalTemplate?: boolean }) =>
        opts?.excludeGlobalTemplate ?? false,
    (state: RootState, templateId: string) => getTemplateEnabledGlobalTemplates(state, templateId),
    (
        displayGroups,
        templateId,
        excludeGlobalTemplate,
        enabledGlobalTemplates,
    ): readonly StoredDisplayGroup[] => {
        const displayGroupList = displayGroupsOrm(displayGroups).filter((item) => {
            if (item.template === templateId) {
                return true;
            }
            if (excludeGlobalTemplate) {
                return false;
            }
            return enabledGlobalTemplates.includes(item.template);
        });
        return withStableEmptyArray(displayGroupList.sort(byDisplayGroupOrder));
    },
);

// Dynamically create display groups that includes multiple speakers display group.
export const getDynamicTemplateDisplayGroups = createSelector(
    (state: RootState) => state.templates.displayGroups,
    (state: RootState, templateId: string) => templateId,
    (displayGroups, templateId): readonly StoredDisplayGroup[] => {
        const templateDisplayGroups = displayGroupsOrm(displayGroups).filter(
            (item) => item.template === templateId,
        );

        if (templateDisplayGroups.length === 0) {
            return withStableEmptyArray(templateDisplayGroups);
        }

        // Adding the multiple speakers display group to the list, it should always be the second display group
        const uniqueOrders = Array.from(new Set(templateDisplayGroups.map((g) => g.order)));
        uniqueOrders.sort((a, b) => a - b);
        let multipleSpeakersOrder = 1;
        if (uniqueOrders.length === 1) {
            multipleSpeakersOrder = uniqueOrders[0] + 1;
        } else if (uniqueOrders.length > 1) {
            multipleSpeakersOrder = Math.ceil((uniqueOrders[0] + uniqueOrders[1]) / 2);
        }

        const multipleSpeakersDisplayGroup: DisplayGroup = {
            id: DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.MultipleSpeakers],
            display_name: 'Multiple Speakers',
            order: multipleSpeakersOrder,
            template: '',
        };

        // Charts appear at the end. They render only when scenesToShow has chart entries
        // for the group (getDisplayGroupsToShow filters out groups with no scenes).
        const chartDisplayGroup: DisplayGroup = {
            id: DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.Chart],
            display_name: 'Charts',
            order: (uniqueOrders.at(-1) ?? 0) + 1,
            template: '',
        };

        return [...templateDisplayGroups, multipleSpeakersDisplayGroup, chartDisplayGroup].sort(
            byDisplayGroupOrder,
        );
    },
);

export function getDisplayGroupById(
    state: RootState,
    displayGroupId: DisplayGroupId,
): StoredDisplayGroup | undefined {
    return displayGroupsOrm(state.templates.displayGroups).getById(displayGroupId);
}

export function getTemplateDisplayGroupsAmount(state: RootState, templateId: string): number {
    return displayGroupsOrm(state.templates.displayGroups).filter(
        (displayGroup) => displayGroup.template === templateId,
    ).length;
}

export function hasTemplateDisplayGroups(state: RootState, templateId: string): boolean {
    return getTemplateDisplayGroupsAmount(state, templateId) > 0;
}

export function getSceneDesignDisplayGroup(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    return sceneDesign?.display_group;
}

export function getDisplayGroupNameForSceneDesign(state: RootState, sceneDesignId: string): string {
    const displayGroupId = getSceneDesignDisplayGroup(state, sceneDesignId);
    if (!displayGroupId) {
        return '';
    }
    const displayGroup = getDisplayGroupById(state, displayGroupId);
    if (!displayGroup) {
        return '';
    }
    return displayGroup.display_name;
}

export const getGroupedSceneDesignIds = createSelector(
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) => INTERNAL_getTemplateSceneDesigns(state, templateId),
    (state: RootState) => state.templates.displayGroups,
    (sceneDesignIds, sceneDesigns, displayGroups) => {
        const globalIds: string[] = [];
        const groupedIds = new Map<number, string[]>();

        const sceneDesignsMap = new Map(sceneDesigns.map((sd) => [sd.id, sd] as const));

        sceneDesignIds.forEach((id) => {
            const sceneDesign = sceneDesignsMap.get(id);
            if (!sceneDesign) return;

            const isGlobal = isGlobalTemplate(sceneDesign.template);
            if (isGlobal) {
                globalIds.push(id);
            } else {
                const groupId = sceneDesign.display_group;
                const ids = groupedIds.get(groupId) ?? [];
                groupedIds.set(groupId, [...ids, id]);
            }
        });

        const groups = [];

        const groupsOrm = displayGroupsOrm(displayGroups);
        Array.from(groupedIds.entries())
            .sort(([a], [b]) => a - b)
            .forEach(([groupId, ids]) => {
                const group = groupsOrm.getById(groupId);
                const label = group?.display_name || `Display Group ${groupId}`;
                groups.push({ label, options: ids });
            });

        if (globalIds.length > 0) {
            groups.push({ label: 'Global Designs', options: globalIds });
        }

        return groups;
    },
);

export function getRecentSelectedSceneDesigns(state: RootState): readonly string[] {
    return state.templates.recentSelectedSceneDesigns;
}

export const getCappedRecentSelectedSceneDesigns = createSelector(
    (state: RootState) => getRecentSelectedSceneDesigns(state),
    (recentlySelectedSceneDesigns: ReturnType<typeof getRecentSelectedSceneDesigns>) => {
        const maxRecentDesignsCount = 3;
        return recentlySelectedSceneDesigns.slice(0, maxRecentDesignsCount);
    },
);

export function getActiveTemplateCategoryId(state: RootState): TemplateCategoryId | null {
    return state.templates.activeTemplateCategoryId;
}

export function isCustomTemplate(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return !!template && template.teams.length > 0;
}

export function hasACustomTemplate(state: RootState): boolean {
    return (
        templatesListOrm(state.templates.templatesList).find(
            (template) => template.teams.length > 0,
        ) !== undefined
    );
}

export function getTemplateColorShuffleEnabled(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return !!template && template.color_shuffle_enabled;
}

export function getTemplateIsGlobal(state: RootState, templateId: string): boolean {
    const template = getTemplateById(state, templateId);
    return !!template && !!template.is_global_template;
}

export function getTemplateEnabledGlobalTemplates(
    state: RootState,
    templateId: string,
): readonly string[] {
    return selectStoredTemplate(state, templateId)?.enabled_global_templates ?? EMPTY_STRINGS;
}

export const getGlobalTemplateIds = createSelector(
    [(state: RootState) => state.templates.templatesList],
    (templatesList): readonly string[] =>
        withStableEmptyArray(
            templatesListOrm(templatesList)
                .filter((template) => !!template.is_global_template)
                .map((template) => template.id),
        ),
);

export const getNonGlobalTemplateIds = createSelector(
    [(state: RootState) => state.templates.templatesList],
    (templatesList): readonly string[] =>
        withStableEmptyArray(
            templatesListOrm(templatesList)
                .filter((template) => !template.is_global_template)
                .map((template) => template.id),
        ),
);

export const getTemplateIdsWithGlobalTemplateEnabled = createSelector(
    [
        (state: RootState) => state.templates.templatesList,
        (_state: RootState, globalTemplateId: string) => globalTemplateId,
    ],
    (templatesList, globalTemplateId): readonly string[] =>
        withStableEmptyArray(
            templatesListOrm(templatesList)
                .filter(
                    (template) => !!template.enabled_global_templates?.includes(globalTemplateId),
                )
                .map((template) => template.id),
        ),
);

export function getTemplateDefaultAspectRatio(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    if (!template) return null;
    return template.default_aspect_ratio as AspectRatio | null;
}

export const getTemplateIdsForClipCreator = createSelector([getTemplateIds], (templateIds) => {
    return templateIds.filter((templateId) => templateId !== CLIP_CREATOR_PREVIEW_TEMPLATE);
});

export function getTemplateSearchQuery(state: RootState): string {
    return state.templates.templateSearchQuery;
}

export function isSceneDesignSelected(state: RootState, sceneDesignId: string): boolean {
    return !!state.templates.sceneDesignSelection.selectedIds[sceneDesignId];
}

export function hasAnySceneDesignSelected(state: RootState): boolean {
    return Object.keys(state.templates.sceneDesignSelection.selectedIds).length > 0;
}

export function getSelectedSceneDesignIds(state: RootState): string[] {
    return Object.keys(state.templates.sceneDesignSelection.selectedIds);
}

/**
 * Gets all template IDs that match the search query, across all categories
 */
export const getAllFilteredTemplateIds = createSelector(
    [
        (state: RootState) => state.templates.templatesList.byId,
        (state: RootState) => getTemplateCategoryIds(state),
        (state: RootState) => state.templates.templateCategories.byId,
        (state: RootState) => getTemplateSearchQuery(state),
    ],
    (allTemplates, categoryIds, allCategories, searchQuery): readonly string[] => {
        if (!searchQuery.trim()) {
            return EMPTY_STRINGS;
        }

        // Split query by whitespace and filter out empty strings
        const queryWords = searchQuery
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0);

        // Collect all template IDs from all categories
        const allTemplateIds = new Set<string>();
        categoryIds.forEach((categoryId) => {
            const category = allCategories[categoryId];
            if (category) {
                const templateIds = category.templates || [];
                templateIds.forEach((templateId) => {
                    allTemplateIds.add(templateId);
                });
            }
        });

        // Filter based on search query
        const filteredIds = Array.from(allTemplateIds).filter((templateId) => {
            const template = allTemplates[templateId] as StoredTemplate | undefined;
            if (!template) return false;

            const categoryId = template.template_category;
            const categoryName = allCategories[categoryId].display_name || '';
            const searchableText = [
                template.display_name || '',
                template.description || '',
                templateId,
                categoryName,
            ]
                .join(' ')
                .toLowerCase();

            return queryWords.every((word) => searchableText.includes(word));
        });

        return withStableEmptyArray(filteredIds);
    },
);

export function isGlobalTemplate(templateId: string): boolean {
    return templateId === (GlobalTemplate.GLOBAL_TEMPLATE_ID as string);
}

const SCENE_CATEGORIES_MANAGED_BY_LUMINARY: string[] = [
    VideoData.CATEGORY_OUTRO,
    VideoData.CATEGORY_BRAND,
];

/**
 * A global template scene design to build a scene from when the scene's own content is applied
 * afterwards and the design itself doesn't matter. The global template is the only one guaranteed to
 * define every aspect ratio, which makes it the one usable for a scene in an aspect ratio the
 * video's own template may not support.
 */
export function getGlobalTemplateSceneDesignId(state: RootState): string | null {
    const globalTemplateId = GlobalTemplate.GLOBAL_TEMPLATE_ID as string;
    const supportedAspectRatios = getSupportedAspectRatios(state, globalTemplateId);
    if (supportedAspectRatios.length === 0) {
        return null;
    }
    return (
        getTemplateSceneIds(state, globalTemplateId).find((sceneDesignId) => {
            const sceneDesign = getSceneDesign(state, sceneDesignId);
            if (!sceneDesign) {
                return false;
            }
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            return !SCENE_CATEGORIES_MANAGED_BY_LUMINARY.includes(sceneCategory);
        }) ?? null
    );
}

export function luminaryScenesHaveTemplateReferences(
    luminary: Readonly<Luminary>,
    sceneDesignIds: string[],
): boolean {
    return luminary.scenes
        .filter((scene) => sceneDesignIds.includes(scene.sceneDesign))
        .some((scene) => {
            const referenceMap = scene.buildTemplateReferenceMap();
            return Object.values(referenceMap).some(
                (assetRefs) => Object.keys(assetRefs).length > 0,
            );
        });
}

export const getSceneDesignIdsForSceneCategories = createSelector(
    (state: RootState, templateId: string, sceneCategories: string[]) => sceneCategories,
    (state: RootState, templateId: string) => getTemplateSceneDesigns(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneCategories, allSceneDesigns, supportedAspectRatios) =>
        sceneCategories.flatMap((sceneCategory) =>
            allSceneDesigns
                .filter(
                    (sceneDesign) =>
                        getSceneCategoryFromSceneDesign(sceneDesign, supportedAspectRatios) ===
                        sceneCategory,
                )
                .map((sceneDesign) => sceneDesign.id),
        ),
);

export function getSceneDesignIdsForDisplayGroupNames(
    state: RootState,
    templateId: string,
    displayGroupNames: string[],
): string[] {
    const groupedSceneDesignIds = getGroupedSceneDesignIds(state, templateId);
    return displayGroupNames.flatMap((displayGroupName) => {
        const displayGroup = groupedSceneDesignIds.find(
            (group) => group.label.toLowerCase() === displayGroupName.toLowerCase(),
        );
        return displayGroup?.options ?? [];
    });
}
